import { Injectable } from '@angular/core';
import { Observable } from 'rxjs/Observable';
import { Subject } from 'rxjs/Subject';
import _ from 'lodash';

import { DeviceReferenceService } from './device-reference.service';
import { DevicesService } from '../resources/devices.service';
import { DeviceModel } from '../models/device.model';

type AttrReport = {
  devices: {
    models: string[];
    processors: string[];
  };
  reports: Array<{
    attribute: string;
    config: {
      report: any;
    };
  }>;
};

@Injectable()
export class AttrReportConfigService {
  private static readonly TIME_BETWEEN = 5 * 1000;
  private static readonly ATTR_CONFIG_STATUS = {
    DONE: 'done'
  };

  private processing: boolean = false;
  private cancelSubject: Subject<boolean> = new Subject();
  private initialTimeout: any;

  constructor(
    private deviceReferenceService: DeviceReferenceService,
    private devicesService: DevicesService
  ) { }

  public startConfigurationProcess(devices: DeviceModel[]) {
    if (!this.processing) {
      this.processing = true;
      this.cancelSubject = new Subject();

      this.initialTimeout = setTimeout(() => {
        console.info('Attribute report config started');
        if (this.deviceReferenceService.isLoaded) {
          this.processAttrReports(devices);
        } else {
          this.deviceReferenceService.onLoaded.subscribe((isLoaded) => {
            this.processAttrReports(devices);
          });
        }
      }, AttrReportConfigService.TIME_BETWEEN);
    }
  }

  public stopConfigurationProcess() {
    console.info('Attribute report config stopped');
    this.processing = false;
    this.cancelSubject.next(true);
    if (this.initialTimeout) {
      clearTimeout(this.initialTimeout);
    }
  }

  private processAttrReports(devices: DeviceModel[]) {
    const attrReports: AttrReport[] =
      (this.deviceReferenceService.data &&
        this.deviceReferenceService.data.attrReports) ||
      [];

    const devReportsProcessed = attrReports
      .filter((a) => a.devices && a.reports)
      .reduce((devReports: any, attrReport: AttrReport) => {
        this.filterDevices(attrReport, devices).forEach((d) => {
          const reportsToConfigure = this.filterReports(attrReport, d);
          if (reportsToConfigure.length > 0) {
            const currentReports =
              (devReports[d._id] && devReports[d._id].reports) || [];
            devReports[d._id] = Object.assign({}, devReports[d._id], {
              device: d,
              reports: currentReports.concat(...reportsToConfigure)
            });
          }
        });
        return devReports;
      }, {});

    this.processDevices(devReportsProcessed);
  }

  private filterDevices(
    attrReport: AttrReport,
    devices: DeviceModel[]
  ): DeviceModel[] {
    return devices.filter((d) => {
      return (
        (attrReport.devices.models &&
          attrReport.devices.models.some((m) => d.model === m)) ||
        (attrReport.devices.processors &&
          attrReport.devices.processors.some((p) => !!d.getProcessor(p)))
      );
    });
  }

  private filterReports(attrReport: AttrReport, device: DeviceModel) {
    return attrReport.reports.filter((r) => {
      const attrReportStatus = _.get(
        device,
        `meta.attrReports[${r.attribute}]`
      );
      return (
        attrReportStatus !== AttrReportConfigService.ATTR_CONFIG_STATUS.DONE
      );
    });
  }

  private processDevices(devReports: any) {
    console.info('Attribute report config devices:', devReports);
    if (devReports && Object.keys(devReports).length > 0) {
      Object.keys(devReports)
        .reduce((process: Observable<any>, key: string) => {
          return process
            .flatMap(() => {
              return this.processDevice(devReports[key])
                .catch(() => Observable.of({}))
                .delay(AttrReportConfigService.TIME_BETWEEN);
            })
            .takeUntil(this.cancelSubject.asObservable());
        }, Observable.of({}))
        .subscribe(() => {
          console.info('Attribute report config finished');
          this.processing = false;
        });
    } else {
      this.processing = false;
    }
  }

  private processDevice(devReport: any): Observable<any> {
    const { device, reports } = devReport;
    reports.forEach((r: any) => {
      const processor = device.processors.find(
        (p: any) =>
          p.attributes && p.attributes.some((a: any) => a.name === r.attribute)
      );
      if (processor) {
        const attribute = processor.attributes.find(
          (a: any) => a.name === r.attribute
        );
        attribute.config = Object.assign({}, attribute.config, {
          config: r.config
        });
        return device.setAttributeReport(processor.componentId, processor.name, attribute.name, attribute.config, false)
          .subscribe((response: any) => {
            // console.log('Getting attribute report now', response);
            device.getAttributeReport(processor.componentId, processor.name, attribute.name)
              .subscribe((rsp: any) => {
                // console.log('Retrieved attribute report: ', rsp);
                if (rsp
                  && rsp.data
                  && rsp.data.config
                  && rsp.data.config.hasOwnProperty('Status')
                  && rsp.data.config.Status === 0) {
                  /* Following 2 lines are commented out because they make sure the exact configuration is matched.
                     However an API user may have chosen a different value in configuration which would still be valid. */
                  // delete rsp.data.config["Status"];
                  // if (_.isEqual(rsp.data.config, config.config)) {
                  // console.log('Attr report written successfully!');
                  _.set(device, `meta.attrReports.${r.attribute}`, AttrReportConfigService.ATTR_CONFIG_STATUS.DONE);
                } else {
                  console.log('Attr report failed');
                  return Observable.empty();
                }
              });
          }); // true, 3000);
      }
    });
    return Observable.empty();
  }
}
